/**
 * Frontend-safe schemas for the raw files inside an evidence bundle
 * (`artifacts/runs/<run-id>/`, see `docs/03_SYSTEM_ARCHITECTURE.md` section
 * 10). `apps/web`'s loader (`apps/web/src/lib/load-bundle.ts`) fetches each
 * file and validates it against the schema here before any view model is
 * built from it — a malformed or missing file degrades to an explicit error
 * state rather than throwing mid-render.
 *
 * `trace.json`, `effects.json`, `alignment.json`, and `divergence.json` are
 * validated against the real, already-reviewed backend schemas
 * (`@exploitlens/trace-ir`, `@exploitlens/effects`, `@exploitlens/aligner`) —
 * those three packages have no Node-only dependency chain (confirmed by a
 * clean `pnpm --filter @exploitlens/web build`), so importing them directly
 * keeps this package's validation identical to what actually wrote the
 * bytes on disk.
 *
 * `manifest.json`, `receipt.json`, and `variants/ledger.json` are validated
 * against field-for-field mirrors defined here instead of the real
 * `@exploitlens/manifest` / `@exploitlens/receipt` / `@exploitlens/variants`
 * schemas — see `schema.ts`'s `GateResultSchema` doc for why those three
 * packages cannot be imported into a browser bundle. Every field below
 * traces back to `packages/manifest/src/schema.ts`, `packages/receipt/src/
 * schema.ts`, and `packages/variants/src/schema.ts` respectively; none is
 * invented.
 */
import { AlignmentFileSchema, AlignmentRelationSchema, DivergenceFileSchema, } from "@exploitlens/aligner";
import { EffectsFileSchema } from "@exploitlens/effects";
import { TraceSchema } from "@exploitlens/trace-ir";
import { z } from "zod";
import { GateResultSchema } from "./schema.js";
export { AlignmentFileSchema, AlignmentRelationSchema, DivergenceFileSchema, EffectsFileSchema, TraceSchema, };
const HexAddressSchema = z
    .string()
    .regex(/^0x[0-9a-fA-F]{40}$/, "must be a 20-byte 0x-prefixed hex address");
const HexHashSchema = z
    .string()
    .regex(/^0x[0-9a-fA-F]{64}$/, "must be a 32-byte 0x-prefixed hex hash");
const GitCommitShaSchema = z
    .string()
    .regex(/^[0-9a-f]{40}$/i, "must be a 40-character git commit SHA");
const IsoTimestampSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/, "must be a UTC ISO-8601 timestamp ending in Z");
/** Mirrors `@exploitlens/manifest`'s `ExpectedExploitSchema`. */
export const ExpectedExploitViewSchema = z.object({
    effectType: z.string().min(1),
    asset: z.string().optional(),
    minimumDelta: z.string().regex(/^\d+$/).optional(),
    attackerAddress: HexAddressSchema.optional(),
});
/** Mirrors `@exploitlens/manifest`'s `InvariantRefSchema`. */
export const InvariantRefViewSchema = z.object({
    id: z.string().min(1),
    description: z.string().min(1),
    evaluator: z.string().min(1),
});
/** Mirrors `@exploitlens/manifest`'s `VariantConfigSchema`. */
export const VariantConfigViewSchema = z.object({
    strategy: z.string().min(1),
    count: z.number().int().positive(),
    seed: z.string().min(1),
});
/** Mirrors the fields of `@exploitlens/manifest`'s `ExploitBundleSchema` the UI actually renders (`manifest.json`). */
export const ManifestViewSchema = z.object({
    schemaVersion: z.literal("1.0"),
    caseId: z.string().min(1),
    repository: z.object({
        path: z.string().min(1),
        baselineCommit: GitCommitShaSchema,
        patchedCommit: GitCommitShaSchema,
    }),
    foundry: z.object({
        testPath: z.string().min(1),
        testSelector: z.string().min(1),
        controlSelector: z.string().min(1),
    }),
    expectedExploit: ExpectedExploitViewSchema,
    invariant: InvariantRefViewSchema,
    variants: VariantConfigViewSchema,
});
/** Mirrors `@exploitlens/receipt`'s `ReceiptScopeSchema`. */
export const ReceiptScopeSchema = z.object({
    claim: z.string().min(1),
    limitations: z.array(z.string().min(1)),
});
/** Mirrors `@exploitlens/receipt`'s `ReceiptSchema` (`receipt.json`). */
export const ReceiptViewSchema = z.object({
    schemaVersion: z.literal("1.1"),
    caseId: z.string().min(1),
    manifestHash: HexHashSchema,
    repository: z.object({
        baselineCommit: GitCommitShaSchema,
        patchedCommit: GitCommitShaSchema,
    }),
    gates: z.array(GateResultSchema).min(1),
    variantDenominator: z.number().int().nonnegative(),
    scope: ReceiptScopeSchema,
    evidenceRefs: z.array(z.string().min(1)).min(1),
    generatedAt: IsoTimestampSchema,
});
/** Mirrors `@exploitlens/receipt`'s `ArtifactEntrySchema`/`ArtifactIndexSchema` (`artifact-index.json`). */
export const ArtifactEntryViewSchema = z.object({
    path: z.string().min(1),
    sha256: HexHashSchema,
    bytes: z.number().int().nonnegative(),
});
export const ArtifactIndexViewSchema = z.object({
    schemaVersion: z.literal("1.0"),
    caseId: z.string().min(1),
    generatedAt: IsoTimestampSchema,
    files: z.array(ArtifactEntryViewSchema).min(1),
});
/** Mirrors `@exploitlens/variants`'s `VariantCandidateSchema`. */
export const VariantCandidateViewSchema = z.object({
    id: z.string().min(1),
    strategy: z.enum(["amount", "repetition", "timing", "actor", "boundary"]),
    seed: z.string().min(1),
    parameters: z.record(z.string(), z.unknown()),
});
/** Mirrors `@exploitlens/variants`'s `VariantResultSchema`. */
export const VariantResultViewSchema = z.object({
    candidate: VariantCandidateViewSchema,
    vulnerableValidity: z.enum(["valid", "invalid"]),
    invalidReason: z.string().min(1).optional(),
    patchedStatus: z.enum(["pass", "fail", "inconclusive"]).optional(),
});
/** Mirrors `@exploitlens/variants`'s `VariantLedgerSchema` (`variants/ledger.json`). */
export const VariantLedgerViewSchema = z.object({
    schemaVersion: z.literal("1.0"),
    caseId: z.string().min(1),
    seed: z.string().min(1),
    budget: z.number().int().positive(),
    results: z.array(VariantResultViewSchema),
    validDenominator: z.number().int().nonnegative(),
});
/** Mirrors `@exploitlens/verifier`'s `VerificationIssue`/`VerificationReport` shape. */
export const VerificationIssueViewSchema = z.object({
    check: z.enum([
        "required-file",
        "schema",
        "hash",
        "internal-reference",
        "gate-consistency",
        "receipt-scope",
    ]),
    path: z.string(),
    message: z.string(),
});
/**
 * The pre-generated verifier result the loader fetches statically
 * (`verification.json` / `verification.tampered.json`, see
 * `scripts/generate-evidence-bundle.ts`). This app never runs
 * `@exploitlens/verifier`'s `verifyBundle` itself — that package sits on the
 * same Node-only chain documented in `schema.ts` — so the bundle generator
 * runs it once, in Node, and ships the real result as static JSON.
 */
export const VerificationResultViewSchema = z.object({
    label: z.string().min(1),
    bundleDir: z.string().min(1),
    ok: z.boolean(),
    issues: z.array(VerificationIssueViewSchema),
});
/**
 * One real Telegraph call, as safe to render. Deliberately a subset of
 * `@exploitlens/intelligence`'s real `AttestedSignal`: that package sits on
 * the same Node-only chain documented in `schema.ts`, so this is
 * independently defined rather than imported, same as
 * `VerificationResultView` above. Only the `normalized` fields are carried
 * -- the real schema's own doc comment warns the raw `result` field is
 * "unsanitized by design" and must never be rendered as anything but
 * escaped text, so this view omits it entirely rather than risk a caller
 * rendering it unsafely.
 */
export const IntelligenceSignalViewSchema = z.object({
    intent: z.string().min(1),
    minerId: z.string().min(1),
    minerName: z.string().min(1),
    signalHash: z.string().min(1),
    requestedAt: z.string().min(1),
    network: z.string().min(1),
    paymentStatus: z.enum(["settled", "not-required"]),
    summary: z.string().optional(),
});
/**
 * The pre-generated intelligence attestation the loader fetches statically
 * (`intelligence.json`, optional -- most cases won't have one). Explicitly
 * a sidecar: nothing in this view, or anywhere this app renders it, may be
 * presented as part of the deterministic verdict. See
 * `components/receipt/IntelligenceAttestation.tsx`'s own disclaimer copy.
 */
export const IntelligenceAttestationViewSchema = z.object({
    caseId: z.string().min(1),
    bundleDigest: z.string().min(1),
    generatedAt: z.string().min(1),
    disclaimer: z.string().min(1),
    signals: z.array(IntelligenceSignalViewSchema),
});
/** Anchor's exact public risk-check verdicts. They are display context only and are never remapped to an ExploitLens gate status. */
export const AnchorVerdictSchema = z.enum(["ALLOW", "RECHECK", "BLOCK"]);
/**
 * Exact response contract for Anchor's public Aave v3 / Base mainnet
 * counterparty-risk endpoint. `strict()` is deliberate: if Anchor changes
 * the contract, the browser shows an unavailable state instead of silently
 * inventing an interpretation. This is external intelligence only; it is
 * never read by bundle generation, receipt generation, or verification.
 */
export const AnchorRiskResponseSchema = z
    .object({
    wallet: HexAddressSchema,
    protocol: z.string().min(1),
    verdict: AnchorVerdictSchema,
    reasoning: z.string().min(1),
    confidence: z.number().finite(),
    signals: z
        .object({
        riskLabel: z.string().min(1),
        healthFactor: z.number().finite().nullable(),
        liquidationDistancePercent: z.number().finite().nullable(),
        totalCollateralUSD: z.number().finite(),
        totalDebtUSD: z.number().finite(),
        liquidationThreshold: z.number().finite(),
    })
        .strict(),
    meta: z
        .object({
        blockNumber: z.number().int().nonnegative(),
        timestamp: z.string().min(1),
        source: z.string().min(1),
        chainId: z.number().int().positive(),
        network: z.string().min(1),
    })
        .strict(),
})
    .strict();
//# sourceMappingURL=artifacts.js.map